'use strict';

// Converts TabularDuctData's stored shape to a plain 2D grid of numbers and back.
//
// stored:  columns: [{value}, ...]                (column headers; leading row-key columns are usually 0)
//          rows:    [{cells: [{value}, ...]}, ...] (one cell per column)
// grid:    [[header values...], [row 1 values...], [row 2 values...], ...]
//
// fromGrid() keeps any other keys that were on the original column/cell objects at the same position.
angular.module('enervexSalesappApp').factory('DuctDataGridUtil', function(_) {
	function num(v) {
		if (_.isNumber(v)) return v;
		if (v === null || v === undefined || v === '') return null;
		var n = parseFloat(v);
		return _.isNaN(n) ? null : n;
	}
	var TYPES = [{
		value: 'junction',
		name: 'Junction (tee / wye)',
		keyColumns: 2
	}, {
		value: 'transition',
		name: 'Transition (size change)',
		keyColumns: 1
	}, {
		value: 'other',
		name: 'Other / not used by the calculation',
		keyColumns: 0
	}];

	// Axis labels for one table. Junction tables are per inlet, named by suffix:
	// Cs = straight-through inlet, Cb = branch inlet (tees), Cb1 / Cb2 = inlet 1 / 2 (bullheads, wyes).
	function describe(ductDataType, code) {
		code = code || '';
		if (ductDataType == 'junction') {
			var inlet, flow, keys;
			if (/Cb1$/.test(code) || /Cb2$/.test(code)) {
				var n = /Cb1$/.test(code) ? '1' : '2';
				inlet = 'inlet ' + n;
				flow = 'Q' + n + '/Q0';
				keys = ['A1/A0', 'A2/A0'];
			} else if (/Cs$/.test(code)) {
				inlet = 'the straight-through inlet';
				flow = 'Qs/Q0';
				keys = ['As/A0 (straight)', 'Ab/A0 (branch)'];
			} else {
				inlet = 'the branch inlet';
				flow = 'Qb/Q0';
				keys = ['As/A0 (straight)', 'Ab/A0 (branch)'];
			}
			return {
				keyColumns: 2,
				keyLabels: keys,
				headerLabel: 'Flow split ' + flow + ' (' + inlet + ' mass flow / combined outlet flow)',
				cellLabel: 'Loss coefficient C for ' + inlet,
				// getValueByRows clamps the flow split to 0.1 - 0.9
				usedRange: [0.1, 0.9],
				help: 'Each row is one combination of inlet-to-outlet area ratios (A0 = outlet). Columns are the share of the ' +
					'combined flow coming from ' + inlet + '. Cells are the loss coefficient C added to ' + inlet + '. ' +
					'Rows must stay sorted by the first key, then the second. Columns outside 0.1 - 0.9 are only reached by interpolation at the edges.'
			};
		} else if (ductDataType == 'transition') {
			var ratio = /RDN$/.test(code) ? 'A/Ain (this section / upstream section)' : 'Ain/A (upstream section / this section)';
			return {
				keyColumns: 1,
				keyLabels: ['Area ratio ' + ratio],
				headerLabel: 'Taper angle in degrees (60 = reducer / taper, 180 = abrupt change)',
				cellLabel: 'Loss coefficient C',
				usedRange: null,
				help: 'Each row is an area ratio between the upstream section and this one. Columns are the taper angle. ' +
					'Cells are the loss coefficient C added to the upstream section. ' +
					(/RDN$/.test(code) ? 'Used for sections downstream of an inline / ECO fan.' : 'Used for sections that are not downstream of a fan.') +
					' Rows must stay sorted by area ratio.'
			};
		}
		return {
			keyColumns: 0,
			keyLabels: [],
			headerLabel: null,
			cellLabel: null,
			usedRange: null,
			help: 'This table is not looked up by the calculation, so its axes have no defined meaning. The first row holds the column headers.'
		};
	}

	return {
		toGrid: function(item) {
			var columns = item.columns || [];
			var rows = item.rows || [];
			var width = Math.max(columns.length, _.max(_.map(rows, function(r) {
				return (r.cells || []).length;
			}).concat([0])));
			var header = _.times(width, function(c) {
				return columns[c] ? num(columns[c].value) : null;
			});
			var body = _.map(rows, function(r) {
				var cells = r.cells || [];
				return _.times(width, function(c) {
					return cells[c] ? num(cells[c].value) : null;
				});
			});
			return [header].concat(body);
		},
		fromGrid: function(grid, original) {
			var origColumns = (original && original.columns) || [];
			var origRows = (original && original.rows) || [];
			var header = grid[0] || [];
			var columns = _.map(header, function(v, c) {
				return _.extend({}, _.isObject(origColumns[c]) ? origColumns[c] : {}, { value: num(v) });
			});
			var rows = _.map(grid.slice(1), function(r, i) {
				var orig = _.isObject(origRows[i]) ? origRows[i] : {};
				var origCells = orig.cells || [];
				return _.extend({}, orig, {
					cells: _.map(r, function(v, c) {
						return _.extend({}, _.isObject(origCells[c]) ? origCells[c] : {}, { value: num(v) });
					})
				});
			});
			return { columns: columns, rows: rows };
		},
		// tab/comma separated text (e.g. pasted from Excel) -> grid; first line is the header row
		parse: function(text) {
			var lines = _.filter((text || '').split(/\r?\n/), function(l) {
				return l.trim() != '';
			});
			var grid = _.map(lines, function(l) {
				return _.map(l.split(l.indexOf('\t') >= 0 ? '\t' : /[,\s]+/), function(v) {
					return num(v.trim());
				});
			});
			var width = _.max(_.map(grid, 'length').concat([0]));
			return _.map(grid, function(r) {
				return _.times(width, function(c) {
					return r[c] === undefined ? null : r[c];
				});
			});
		},
		toText: function(grid) {
			return _.map(grid, function(r) {
				return _.map(r, function(v) {
					return v === null || v === undefined ? '' : v;
				}).join('\t');
			}).join('\n');
		},
		num: num,
		// how each ductDataType grid is read by the calculation; keep in sync with
		// TabularDuctData.DUCT_DATA_TYPES on the server
		types: TYPES,
		describe: describe
	};
});
